import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Modal, ModalContent, ModalOverlay, Spinner } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

import { PDFView } from './PDFView'
import { PreviewIcon } from './PreviewIcon'
import { httpUrl } from './resource'
import { usePreview } from './usePreview'
import { usePrint } from './usePrint'
import { nextZoom, pageNumber } from './viewUtils'

import type { FilePreviewProps, PreviewState } from './types'

import './FilePreview.scss'

export function FilePreview(props: FilePreviewProps) {
    const key = JSON.stringify(
        props.folder === 'tmp-apply-file'
            ? { folder: props.folder, uid: props.uid, fileUid: props.fileUid, type: props.type }
            : { folder: props.folder, fileId: props.fileId }
    )
    const auth = useMemo(() => JSON.parse(key) as FilePreviewProps, [key])
    return <PreviewSession key={key} auth={auth} />
}

function PreviewSession({ auth }: { auth: FilePreviewProps }) {
    const [closed, setClosed] = useState(false)
    return closed ? null : <PreviewDialog auth={auth} onClose={() => setClosed(true)} />
}

function PreviewDialog({ auth, onClose }: { auth: FilePreviewProps; onClose: () => void }) {
    const { t } = useTranslation()
    const labels = {
        page: String(t('7504', 'Page') ?? 'Page'),
        zoomIn: String(t('13232', 'Zoom in') ?? 'Zoom in'),
        zoomOut: String(t('13233', 'Zoom out') ?? 'Zoom out'),
        fit: String(t('9348', 'Fit to screen') ?? 'Fit to screen'),
        restore: String(t('9349', 'Restore previous zoom') ?? 'Restore previous zoom'),
        askAI: String(t('9034', 'Ask AI') ?? 'Ask AI'),
        askAIUnavailable: String(t('13234', 'Ask AI is currently unavailable') ?? 'Ask AI is currently unavailable'),
        preview: String(t('13235', 'File preview') ?? 'File preview'),
        error: String(t('13238', 'Unable to load this file') ?? 'Unable to load this file'),
        print: String(t('1021', 'Print') ?? 'Print'),
        download: String(t('2342', 'Download') ?? 'Download'),
        close: String(t('3723', 'Close') ?? 'Close'),
        unavailable: String(t('9351', 'Preview is not available') ?? 'Preview is not available'),
        denied: String(t('248', 'No access') ?? 'No access'),
        unsupported: String(t('6200', 'File format is not supported') ?? 'File format is not supported'),
    }
    const pageId = useId()
    const state = usePreview(auth)
    const containerRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const currentPageRef = useRef(1)
    const previousZoomRef = useRef(100)
    const [metrics, setMetrics] = useState({ width: 0, pages: 0 })
    const [zoom, setZoom] = useState({ value: 100, fitted: false, previous: 100 })
    const [page, setPage] = useState(1)
    const [pageInput, setPageInput] = useState('1')
    const [renderError, setRenderError] = useState(false)
    const print = usePrint(state.url, state.kind, labels.print)
    const ready = state.status === 'ready' && metrics.width > 0 && !renderError
    const canPrint = ready && (state.kind === 'pdf' || /Chrome/.test(navigator.userAgent))
    const downloadUrl = state.metadata?.downloadUrl ? httpUrl(state.metadata.downloadUrl) : undefined
    const changeZoom = useCallback((direction: 'in' | 'out') => {
        setZoom((previous) => ({ ...previous, fitted: false, value: nextZoom(previous.value, direction) }))
    }, [])
    const fitZoom = useCallback(
        (width: number) => Math.max(1, Math.floor((((containerRef.current?.clientWidth || width) - 65) / width) * 100)),
        []
    )
    const onReady = useCallback(
        (width: number, pages: number) => {
            setMetrics({ width, pages })
            const value = Math.min(100, fitZoom(width))
            setZoom({ value, fitted: value < 100, previous: 100 })
        },
        [fitZoom]
    )
    const onError = useCallback(() => setRenderError(true), [])
    const onPage = useCallback((current: number) => {
        currentPageRef.current = current
        setPage(current)
        setPageInput(String(current))
    }, [])

    useLayoutEffect(() => {
        const container = scrollRef.current
        if (container && previousZoomRef.current !== zoom.value) {
            const item = container.querySelector<HTMLElement>(
                state.kind === 'pdf' ? `[data-page="${currentPageRef.current}"]` : 'img'
            )
            if (item) {
                container.scrollTo({
                    top: item.offsetTop + item.offsetHeight / 2 - container.clientHeight / 2,
                    left: item.offsetLeft + item.offsetWidth / 2 - container.clientWidth / 2,
                    behavior: 'auto',
                })
            }
        }
        previousZoomRef.current = zoom.value
    }, [zoom.value, state.kind])

    useEffect(() => {
        const observer = new ResizeObserver(() => {
            if (metrics.width) {
                setZoom((previous) => (previous.fitted ? { ...previous, value: fitZoom(metrics.width) } : previous))
            }
        })
        if (containerRef.current) observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [metrics.width, fitZoom])

    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (!(event.ctrlKey || event.metaKey) || !ready) return
            if (['+', '=', '-', '_'].includes(event.key)) {
                event.preventDefault()
                changeZoom(['+', '='].includes(event.key) ? 'in' : 'out')
            } else if (event.key.toLowerCase() === 'p' && canPrint) {
                event.preventDefault()
                print()
            }
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [ready, canPrint, print, changeZoom])

    const fit = () =>
        setZoom((previous) =>
            previous.fitted
                ? { value: previous.previous, previous: previous.value, fitted: false }
                : { value: fitZoom(metrics.width), previous: previous.value, fitted: true }
        )
    const selectPage = () => {
        const selected = pageNumber(pageInput, metrics.pages, page)
        onPage(selected)
        const node = scrollRef.current?.querySelector<HTMLElement>(`[data-page="${selected}"]`)
        if (node) scrollRef.current?.scrollTo({ top: node.offsetTop - 24, behavior: 'auto' })
    }
    const created = state.metadata?.created ? new Date(state.metadata.created.replace(' ', 'T')) : null
    const messages: Partial<Record<PreviewState['status'], string>> = {
        denied: labels.denied,
        error: labels.error,
        unsupported: labels.unsupported,
        unavailable: labels.unavailable,
    }
    const message = renderError ? labels.unavailable : messages[state.status]
    const pending = !renderError && ['authorizing', 'loading', 'ready'].includes(state.status) && !ready

    return (
        <Modal
            isOpen
            onClose={onClose}
            size="full"
            motionPreset="none"
            closeOnOverlayClick={false}
            initialFocusRef={closeRef}
        >
            <ModalOverlay bg="rgba(0, 0, 0, 0.3)" />
            <ModalContent
                className="CandidateFilePreview"
                margin={0}
                borderRadius={0}
                maxWidth="100vw"
                height="100dvh"
                minHeight={0}
                padding={0}
                aria-label={state.metadata?.name || labels.preview}
            >
                <div className="CandidateFilePreview__header">
                    <div className="CandidateFilePreview__details">
                        {state.metadata ? (
                            <>
                                <h2 title={state.metadata.name}>{state.metadata.name}</h2>
                                {created && !Number.isNaN(created.getTime()) && (
                                    <span>
                                        {created.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="CandidateFilePreview__skeleton" />
                        )}
                    </div>
                    {ready && state.kind === 'pdf' && (
                        <div className="CandidateFilePreview__pages">
                            <label htmlFor={pageId}>{labels.page}</label>
                            <input
                                id={pageId}
                                type="text"
                                inputMode="numeric"
                                aria-label={labels.page}
                                value={pageInput}
                                onChange={(event) => setPageInput(event.target.value)}
                                onBlur={selectPage}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') selectPage()
                                }}
                            />
                            <span>/ {metrics.pages}</span>
                        </div>
                    )}
                    <div className="CandidateFilePreview__actions">
                        {state.metadata && (
                            <button
                                type="button"
                                className="CandidateFilePreview__button CandidateFilePreview__ai"
                                disabled
                                title={labels.askAIUnavailable}
                            >
                                <PreviewIcon name="stars" /> {labels.askAI}
                            </button>
                        )}
                        {ready && (
                            <div className="CandidateFilePreview__zoom">
                                <button
                                    type="button"
                                    className="CandidateFilePreview__button"
                                    aria-label={labels.zoomOut}
                                    title={labels.zoomOut}
                                    disabled={zoom.value <= 15}
                                    onClick={() => changeZoom('out')}
                                >
                                    <PreviewIcon name="minus" />
                                </button>
                                <span>{zoom.value}%</span>
                                <button
                                    type="button"
                                    className="CandidateFilePreview__button"
                                    aria-label={labels.zoomIn}
                                    title={labels.zoomIn}
                                    disabled={zoom.value >= 200}
                                    onClick={() => changeZoom('in')}
                                >
                                    <PreviewIcon name="plus" />
                                </button>
                                <button
                                    type="button"
                                    className="CandidateFilePreview__button"
                                    aria-label={zoom.fitted ? labels.restore : labels.fit}
                                    title={zoom.fitted ? labels.restore : labels.fit}
                                    onClick={fit}
                                >
                                    <PreviewIcon name={zoom.fitted ? 'collapse' : 'expand'} />
                                </button>
                            </div>
                        )}
                        {canPrint && (
                            <button
                                type="button"
                                className="CandidateFilePreview__button"
                                aria-label={labels.print}
                                title={labels.print}
                                onClick={print}
                            >
                                <PreviewIcon name="print" />
                            </button>
                        )}
                        {downloadUrl && (
                            <a
                                className="CandidateFilePreview__button"
                                href={downloadUrl}
                                download={state.metadata?.name}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={labels.download}
                                title={labels.download}
                            >
                                <PreviewIcon name="download" />
                            </a>
                        )}
                    </div>
                    <button
                        ref={closeRef}
                        type="button"
                        className="CandidateFilePreview__button CandidateFilePreview__close"
                        aria-label={labels.close}
                        title={labels.close}
                        onClick={onClose}
                    >
                        <PreviewIcon name="close" />
                    </button>
                </div>
                <div className="CandidateFilePreview__content" ref={containerRef}>
                    {state.status === 'ready' && !renderError && state.blob && state.kind === 'pdf' && (
                        <PDFView
                            blob={state.blob}
                            zoom={zoom.value}
                            scrollRef={scrollRef}
                            onReady={onReady}
                            onPage={onPage}
                            onError={onError}
                        />
                    )}
                    {state.status === 'ready' && !renderError && state.kind === 'image' && (
                        <div className="CandidateFilePreview__image-scroll" ref={scrollRef}>
                            <div className="CandidateFilePreview__image-wrapper">
                                <img
                                    src={state.url}
                                    alt={state.metadata?.name || ''}
                                    style={{ width: metrics.width ? (metrics.width * zoom.value) / 100 : undefined }}
                                    onLoad={(event) => onReady(event.currentTarget.naturalWidth, 1)}
                                    onError={onError}
                                />
                            </div>
                        </div>
                    )}
                    {!ready && (
                        <div className="CandidateFilePreview__status" role={pending ? 'status' : 'alert'}>
                            {pending ? (
                                <Spinner size="xl" label={String(t('13237', 'Loading file…') ?? 'Loading file…')} />
                            ) : (
                                <>
                                    <PreviewIcon name="file" />
                                    <h3>{message}</h3>
                                </>
                            )}
                            {!pending && state.metadata && <p>{state.metadata.name}</p>}
                            {!pending && downloadUrl && (
                                <a
                                    className="CandidateFilePreview__download"
                                    href={downloadUrl}
                                    download={state.metadata?.name}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {labels.download}
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </ModalContent>
        </Modal>
    )
}
